// src/LoginPage.js
import React, { useState, useEffect, useRef } from 'react';
import './LoginPage.css';
import { useNavigate } from 'react-router-dom';
import AlertaUrgente from "./components/AlertaUrgente";
import { auth, db } from './firebase';
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendPasswordResetEmail,
  onAuthStateChanged,
  fetchSignInMethodsForEmail,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { ensureUserProfile } from './services/postSignInBootstrap';

// 🔥 Aguarda o doc usuarios/{uid} “aparecer” no cliente (evita perfil vazio até dar refresh)
async function waitUserDoc(uid, tries = 6) {
  const ref = doc(db, 'usuarios', uid);
  for (let i = 0; i < tries; i++) {
    const s = await getDoc(ref);
    if (s.exists()) return s.data();
    await new Promise(r => setTimeout(r, 150)); // ~900ms total
  }
  return null;
}

function LoginPage() {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');

  // Modal genérico
  const [modal, setModal] = useState({ aberto: false, titulo: '', mensagem: '' });

  // Esqueci a senha
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');

  const navigate = useNavigate();
  const adminUID = 'CwveE0ysF6agpZiWmG4beKqokNB3';

  // ===== CONFIG URLs =====
  const PARENT_PAGE_URL = "https://www.termometrooscar.com/termometeiro.html";
  const APP_TOP_URL =
    process.env.NODE_ENV === "production"
      ? "https://previsoesoscar.web.app"
      : window.location.origin;

  const inIframe = () => {
    try { return window.self !== window.top; } catch { return true; }
  };

  // ===== Provider Google (1 instância) =====
  const providerRef = useRef(null);
  if (!providerRef.current) {
    const p = new GoogleAuthProvider();
    p.setCustomParameters({ prompt: 'select_account' });
    providerRef.current = p;
  }

  // ===== Helpers de UI =====
  function abrirModal(titulo, mensagem) {
    setModal({ aberto: true, titulo, mensagem });
  }
  function fecharModal() {
    setModal({ aberto: false, titulo: '', mensagem: '' });
  }

  function abrirEsqueciSenha() {
    setForgotEmail(email || '');
    setForgotMsg('');
    setForgotOpen(true);
  }
  function fecharEsqueciSenha() {
    if (forgotLoading) return;
    setForgotOpen(false);
    setForgotMsg('');
  }

  // ===== Query Params =====
  const params = new URLSearchParams(window.location.search);
  const wantGoogle = params.get('google') === '1';
  const returnTo = params.get('return');

  // ===== Pós-login (decidir para onde ir) =====
  function aposLogin(user) {
    if (!user) return;
    if (!user.emailVerified) {
      abrirModal('Verificação pendente', 'Verifica o teu e-mail antes de fazer login.');
      return;
    }
    // Se estamos no topo com ?return=..., volta para o Weebly
    if (!inIframe() && returnTo) {
      window.location.href = returnTo;
      return;
    }
    // Senão, navega normalmente dentro do app
    if (user.uid === adminUID) navigate('/admin');
    else navigate('/perfil');
  }

  // ===== Auto-redirect se já estiver logado (sessão existente) =====
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) return;
      if (!user.emailVerified) return;
      if (user.uid === adminUID) navigate('/admin');
      else navigate('/perfil');
    });
    return () => unsub();
  }, [navigate]);

  // ===== Quando voltamos do redirect do Google =====
  useEffect(() => {
    (async () => {
      try {
        const res = await getRedirectResult(auth);
        if (res?.user) {
          await ensureUserProfile(res.user);
          await waitUserDoc(res.user.uid); // 🔥 aquece o doc antes de renderizar o perfil
          // ✅ limpa cache para evitar “perfil esqueleto”
          try { sessionStorage.removeItem('usuarioFirestore'); } catch {}
          aposLogin(res.user);
        }
      } catch (e) {
        const code = e?.code;
        // Tratamento específico para duplicidade de credenciais
        if (
          code === 'auth/account-exists-with-different-credential' ||
          code === 'auth/credential-already-in-use' ||
          code === 'auth/email-already-in-use'
        ) {
          abrirModal(
            'Já existe uma conta com este e-mail',
            'Este e-mail já foi registado com outro método (provavelmente e-mail/senha). Faz login com e-mail e senha primeiro.'
          );
        } else {
          console.error('Erro ao processar redirect do Google:', e);
        }
      } finally {
        // limpa flag de redirect iniciado
        sessionStorage.removeItem('g_redirect_started');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Se chegamos no topo com ?google=1, inicia redirect sem clicar de novo =====
  useEffect(() => {
    if (wantGoogle && !inIframe()) {
      const already = sessionStorage.getItem('g_redirect_started');
      if (!already) {
        sessionStorage.setItem('g_redirect_started', '1');
        signInWithRedirect(auth, providerRef.current).catch((e) => {
          console.error('Falha ao iniciar redirect do Google:', e);
          abrirModal('Não foi possível entrar', 'Não foi possível abrir a página de login do Google.');
          sessionStorage.removeItem('g_redirect_started');
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantGoogle]);

  // ===== Login por e-mail/senha =====
  const fazerLogin = async (e) => {
    e.preventDefault();
    try {
      const usuario = await signInWithEmailAndPassword(auth, email, senha);
      await usuario.user.reload();
      aposLogin(usuario.user);
    } catch (erro) {
      console.error('Erro ao fazer login:', erro);
      let msg = 'Erro ao fazer login. Verifique o e-mail e a senha.';
      if (erro.code === 'auth/wrong-password' || erro.code === 'auth/invalid-credential') {
        msg = 'E-mail ou senha incorretos.';
      } else if (erro.code === 'auth/user-not-found') {
        msg = 'Não encontramos uma conta com este e-mail.';
      } else if (erro.code === 'auth/too-many-requests') {
        msg = 'Muitas tentativas. Tenta novamente mais tarde.';
      }
      abrirModal('Não foi possível entrar', msg);
    }
  };

  // ===== Login com Google =====
  const fazerLoginGoogle = async () => {
    const provider = providerRef.current;

    // ✅ Pré-checagem: se o utilizador digitou e-mail e ele tem 'password' (e não 'google.com'),
    // já mostra a mensagem correta e evita abrir popup.
    const emailDigitado = (email || '').trim().toLowerCase();
    if (emailDigitado) {
      try {
        const methods = await fetchSignInMethodsForEmail(auth, emailDigitado);
        const temPassword = methods.includes('password');
        const temGoogle = methods.includes('google.com');
        if (temPassword && !temGoogle) {
          abrirModal(
            'Já existe uma conta com este e-mail',
            'Este e-mail já foi registado com outro método (provavelmente e-mail/senha). Faz login com e-mail e senha primeiro.'
          );
          return;
        }
      } catch {
        // silencioso
      }
    }

    const fallbackRedirect = () => {
      // volta para a página do Weebly após o login — já no perfil
      const RETURN_TO = encodeURIComponent("https://www.termometrooscar.com/termometeiro.html#/perfil"); // ✅ aponta direto pro perfil
      const APP_TOP = "https://previsoesoscar.web.app";
      // abre o redirect leve no topo (não dentro do iframe)
      window.top.location.href = `${APP_TOP}/auth-redirect?return=${RETURN_TO}`;
    };

    try {
      // 1) tenta popup primeiro (melhor UX quando o navegador permite)
      const resultado = await signInWithPopup(auth, provider);

      // cria/normaliza perfil, tal como no "Registrar"
      await ensureUserProfile(resultado.user);
      await waitUserDoc(resultado.user.uid); // 🔥 garante doc antes de navegar
      // ✅ limpa cache para evitar “perfil esqueleto”
      try { sessionStorage.removeItem('usuarioFirestore'); } catch {}

      if (!resultado.user.emailVerified) {
        abrirModal('Verificação pendente', 'Verifica o teu e-mail antes de fazer login.');
        return;
      }

      if (resultado.user.uid === adminUID) navigate('/admin');
      else navigate('/perfil');
    } catch (erro) {
      // 🚫 Conta já existe com outro método → mensagem específica
      const DUP_CODES = new Set([
        'auth/account-exists-with-different-credential',
        'auth/credential-already-in-use',
        'auth/email-already-in-use',
      ]);
      if (DUP_CODES.has(erro?.code)) {
        const emailConflito = erro.customData?.email;
        abrirModal(
          'Já existe uma conta com este e-mail',
          'Este e-mail já foi registado com outro método (provavelmente e-mail/senha). Faz login com e-mail e senha primeiro.'
        );
        try {
          if (emailConflito) {
            const methods = await fetchSignInMethodsForEmail(auth, emailConflito);
            console.log('Métodos já existentes para', emailConflito, methods);
          }
        } catch {}
        return;
      }

      // 🔎 Heurística extra: se vier customData.email, consulta métodos para garantir a mensagem correta
      try {
        const e2 = erro?.customData?.email;
        if (e2) {
          const m = await fetchSignInMethodsForEmail(auth, e2);
          if (m.includes('password') && !m.includes('google.com')) {
            abrirModal(
              'Já existe uma conta com este e-mail',
              'Este e-mail já foi registado com outro método (provavelmente e-mail/senha). Faz login com e-mail e senha primeiro.'
            );
            return;
          }
        }
      } catch {}

      // Erros que justificam fallback imediato para redirect de topo
      const FALLBACKS = new Set([
        'auth/popup-blocked',
        'auth/operation-not-supported-in-this-environment',
        'auth/requests-from-referrer-are-blocked',
        'auth/permission-denied'
      ]);
      if (FALLBACKS.has(erro?.code)) {
        fallbackRedirect();
        return;
      }

      // log técnico só em dev
      if (process.env.NODE_ENV !== 'production') {
        console.error('Erro no login com Google:', erro);
      }

      // ✅ mensagens amigáveis (sem "código: ...")
      let msg = 'Não foi possível entrar com o Google. Tente novamente.';
      if (erro?.code === 'auth/unauthorized-domain') {
        msg = 'Login com Google não está disponível neste domínio.';
      } else if (erro?.code === 'auth/popup-closed-by-user') {
        // usuário fechou o popup; não exibir modal é aceitável
        return;
      } else if (erro?.code === 'auth/cancelled-popup-request') {
        msg = 'Já existe uma janela de login aberta. Fecha-a e tenta de novo.';
      }

      abrirModal('Não foi possível entrar', msg);
    }
  };

  // ===== Esqueci a senha =====
  async function enviarResetSenha(e) {
    e?.preventDefault?.();
    setForgotMsg('');

    const emailTrim = (forgotEmail || '').trim();
    if (!emailTrim) {
      setForgotMsg('Por favor, escreve o teu e-mail.');
      return;
    }

    try {
      setForgotLoading(true);
      await sendPasswordResetEmail(auth, emailTrim);
      setForgotMsg('Se existir uma conta com este e-mail, enviámos um link de recuperação.');
    } catch (erro) {
      console.error('Erro ao enviar e-mail de redefinição:', erro);
      let msg = 'Não foi possível enviar o e-mail de recuperação.';
      if (erro.code === 'auth/user-not-found') {
        msg = 'Não encontramos uma conta com este e-mail.';
      } else if (erro.code === 'auth/invalid-email') {
        msg = 'E-mail inválido.';
      } else if (erro.code === 'auth/too-many-requests') {
        msg = 'Muitas tentativas. Tenta novamente mais tarde.';
      }
      setForgotMsg(msg);
    } finally {
      setForgotLoading(false);
    }
  }

  return (
    <div className="login-container">
      <AlertaUrgente />

      {/* MODAL DE ERRO / AVISO */}
      {modal.aberto && (
        <div className="lp-modal-overlay" role="dialog" aria-modal="true">
          <div className="lp-modal">
            <button className="lp-close" onClick={fecharModal} aria-label="Fechar">×</button>
            <h3>{modal.titulo}</h3>
            <p>{modal.mensagem}</p>
            <div className="lp-actions">
              <button className="lp-btn" onClick={fecharModal}>Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ESQUECI A SENHA */}
      {forgotOpen && (
        <div className="lp-modal-overlay" role="dialog" aria-modal="true">
          <div className="lp-modal">
            <button
              className="lp-close"
              onClick={fecharEsqueciSenha}
              aria-label="Fechar"
              disabled={forgotLoading}
            >
              ×
            </button>

            <h3>Redefinir senha</h3>
            <p>Escreva o e-mail cadastrado e enviaremos um link para redefinir a sua senha.</p>

            <form onSubmit={enviarResetSenha} style={{ marginTop: 10 }}>
              <div className="lp-field">
                <label>E-mail</label>
                <input
                  type="email"
                  className="lp-input"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="seuemail@exemplo.com"
                  disabled={forgotLoading}
                />
              </div>

              {forgotMsg && <div className="lp-inline-note">{forgotMsg}</div>}

              <div className="lp-actions">
                <button
                  type="button"
                  className="lp-btn ghost"
                  onClick={fecharEsqueciSenha}
                  disabled={forgotLoading}
                >
                  Cancelar
                </button>
                <button type="submit" className="lp-btn" disabled={forgotLoading}>
                  {forgotLoading ? 'Enviando…' : 'Enviar e-mail'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="login-logo">
        <img src="https://i.imgur.com/atZYcGp.png" alt="Termometeiro" />
      </div>
      <h1 className="login-title">Bem-Vindo!</h1>

      <form onSubmit={fazerLogin} className="login-form" autoComplete="off">
        <input
          type="email"
          placeholder="Endereço de e-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="login-input2"
          autoComplete="off"
        />
        <input
          type="password"
          placeholder="Senha"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          className="login-input2"
          autoComplete="off"
        />

        <div className="login-link">
          Esqueceu a Senha?{' '}
          <button type="button" className="login-link-btn" onClick={abrirEsqueciSenha}>
            Clique aqui
          </button>
        </div>

        <button type="submit" className="login-button">ENTRAR</button>

        <div className="login-divider">
          <hr /><span>OU</span><hr />
        </div>

        <button type="button" className="login-google" onClick={fazerLoginGoogle}>
          <img src="https://i.imgur.com/FBUnYCq.png" alt="Google" />
          Continuar com o Google
        </button>

        <div className="login-hr-dotted"><hr /></div>

        <div className="login-register">Não tem uma conta?</div>
        <button type="button" className="login-register-button" onClick={() => navigate('/registrar')}>
          REGISTRAR
        </button>
      </form>
    </div>
  );
}

export default LoginPage;
